import { Button, EmptyState, Icon, Input, Skeleton } from '@starter/ui';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useCatalog } from '../catalogs/use-catalog.ts';
import { Facet } from '../components/facet.tsx';
import { type JobFilters, useHideJob, useJobs, useRetryJob } from '../jobs/api.ts';
import { JobFrame } from '../jobs/frame.tsx';
import { authClient } from '../lib/auth.ts';
import { useDebounced } from '../video/api.ts';

/**
 * `/generations` — everything this account has ever made.
 *
 * Not the shelf beside the composer, although it reads the same list: that one
 * is about the thing being made right now, this one is about going back. So
 * the narrowing is the screen's whole shape — what it left behind, whether it
 * is ready, how far back, and the words it was ordered with — and none of the
 * four is a client-side filter over sixty rows (`apps/api/src/routes/jobs.ts`).
 *
 * **The three states are the point, and two of them are not the happy one.**
 * A work that is still going says so and roughly how long; a work that failed
 * says why, **whether it was charged**, and offers the same order again. That
 * last pair is the original's own vocabulary — its gallery has a whole set of
 * strings for a failure, including «charged» and «not charged» and their
 * reasons — and it is the part a library exists for: something went wrong at
 * three in the morning and you want to know what it cost you.
 */
const STATUSES = [
  { value: 'ready', label: 'Ready' },
  { value: 'working', label: 'Still going' },
  { value: 'failed', label: 'Went wrong' },
];

const PERIODS = [
  { value: '1', label: 'Today' },
  { value: '7', label: 'This week' },
  { value: '30', label: 'This month' },
];

function Generations() {
  const [produces, setProduces] = useState('');
  const [status, setStatus] = useState('');
  const [days, setDays] = useState('');
  const [typed, setTyped] = useState('');
  const q = useDebounced(typed);

  const kinds = useCatalog('asset_kinds');
  const jobKinds = useCatalog('job_kinds');
  const hide = useHideJob();
  const retry = useRetryJob();

  const filters: JobFilters = {
    ...(produces ? { produces } : {}),
    ...(status ? { status: status as 'ready' | 'working' | 'failed' } : {}),
    ...(days ? { days: Number(days) } : {}),
    ...(q ? { q } : {}),
  };
  const jobs = useJobs(filters);
  const rows = jobs.data?.jobs ?? [];
  const narrowed = produces !== '' || status !== '' || days !== '' || q !== '';

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center gap-4 py-2">
        <h1
          className="min-w-0 flex-1 font-display text-[28px] leading-[34px] font-bold tracking-[-0.7px] text-ink"
          data-fidelity="generations-title"
          data-testid="generations-title"
        >
          My generations
        </h1>
      </header>

      <div className="flex flex-wrap items-center gap-2" data-testid="generations-filters">
        <Input
          aria-label="Search what you have made"
          className="h-9 w-[260px] max-w-full"
          data-testid="generations-search"
          onChange={(event) => setTyped(event.currentTarget.value)}
          placeholder="Search by what you asked for"
          value={typed}
        />
        <Facet
          fidelity="generations-filter"
          label="Anything"
          onChange={setProduces}
          options={kinds.items.map((item) => ({ value: item.key, label: kinds.label(item.key) }))}
          testId="generations-filter-kind"
          value={produces}
          width={214}
        />
        <Facet
          label="Any state"
          onChange={setStatus}
          options={STATUSES}
          testId="generations-filter-status"
          value={status}
          width={214}
        />
        <Facet
          label="All time"
          onChange={setDays}
          options={PERIODS}
          testId="generations-filter-period"
          value={days}
          width={214}
        />
      </div>

      {jobs.isPending ? (
        <Grid>
          {['a', 'b', 'c', 'd', 'e', 'f'].map((slot) => (
            <Skeleton className="aspect-[4/5] w-full rounded-card" key={slot} />
          ))}
        </Grid>
      ) : rows.length === 0 ? (
        <EmptyState
          actions={
            narrowed ? undefined : (
              <Button
                data-fidelity="generations-empty-action"
                render={<Link to="/image-ads" />}
                variant="primary"
              >
                Make the first one
              </Button>
            )
          }
          data-testid="generations-empty"
          description={
            narrowed
              ? 'Nothing here matches all of that. Take one of the filters off.'
              : 'Everything you make lands here — adverts, clips, and the ones that went wrong.'
          }
          descriptionProps={{ 'data-fidelity': 'generations-empty-note' }}
          icon={<Icon name="generations" />}
          title={narrowed ? 'Nothing matches' : 'Nothing made yet'}
          titleProps={{ 'data-fidelity': 'generations-empty-title' }}
          /*
           * `inline`, not the full-page treatment this used. Measured on the
           * original's own library: the title there is 20/24, which is exactly
           * what this variant already carries — and it is the right one by
           * meaning too, because this emptiness sits inside a page that has
           * its own h1 above it (`prd/measured/generations.json`).
           */
          variant="inline"
        />
      ) : (
        <Grid>
          {rows.map((job) => (
            <article
              className="flex flex-col overflow-hidden rounded-card border border-border-subtle bg-surface-raised"
              data-testid="generation-card"
              key={job.id}
            >
              {/*
                `shrink-0`, because a grid stretches its items to the tallest
                in the row and a flex child with an aspect ratio will grow to
                fill it — so a card with three lines of failure under it had a
                *taller picture* than the one beside it. The ratio is the
                promise; the text below takes the slack.
              */}
              <div className="relative aspect-[4/5] w-full shrink-0 bg-surface-muted">
                <JobFrame
                  mime={job.assets[0]?.mime ?? null}
                  status={job.status}
                  uploadId={job.assets[0]?.uploadId ?? null}
                />
                <span
                  className="absolute top-2 left-2 inline-flex items-center rounded-full bg-surface-raised px-2 py-1 text-3xs leading-4 text-ink"
                  data-testid="generation-status"
                >
                  {job.status === 'done'
                    ? 'Ready'
                    : job.status === 'failed'
                      ? 'Went wrong'
                      : 'Working'}
                </span>
              </div>

              <div className="flex flex-1 flex-col gap-1 p-3">
                <span className="text-2xs leading-4 font-semibold text-ink">
                  {jobKinds.label(job.kind)}
                </span>

                {job.status === 'failed' ? (
                  <Failure
                    charged={job.cost !== null && job.cost > 0}
                    onDismiss={() => hide.mutate(job.id)}
                    onRetry={() => retry.mutate(job.id)}
                    pending={retry.isPending && retry.variables === job.id}
                    reason={job.failure}
                    started={retry.isSuccess && retry.variables === job.id}
                  />
                ) : job.status === 'done' ? (
                  <span className="text-3xs leading-4 text-ink-muted">
                    {job.cost === null ? '—' : `${job.cost} credits`} ·{' '}
                    {new Date(job.createdAt).toLocaleDateString()}
                  </span>
                ) : (
                  <span className="text-3xs leading-4 text-ink-muted" data-testid="generation-wait">
                    Queued — about a second
                  </span>
                )}
              </div>
            </article>
          ))}
        </Grid>
      )}

      {jobs.data ? (
        <p className="text-3xs leading-4 text-ink-muted" data-testid="generations-count">
          {`${rows.length} of ${jobs.data.total} shown`}
        </p>
      ) : null}
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4"
      data-testid="generations-grid"
    >
      {children}
    </div>
  );
}

/**
 * What a work says when it did not finish.
 *
 * Three things, in this order: what went wrong, **whether it cost anything**,
 * and the same order again. The middle one is the one people actually want at
 * three in the morning, and it is answerable rather than reassuring — a work
 * that failed never had its cost written, so «nothing was charged» is a fact
 * read off the row rather than a promise (`apps/api/src/routes/jobs.ts`).
 */
function Failure({
  reason,
  charged,
  pending,
  started,
  onRetry,
  onDismiss,
}: {
  reason: string | null;
  charged: boolean;
  pending: boolean;
  /** This one has been started again — said here, on the card that was pressed. */
  started: boolean;
  onRetry: () => void;
  onDismiss: () => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-3xs leading-4 text-danger-text" data-testid="generation-failure">
        {reason ?? 'It did not finish, and did not say why.'}
      </span>
      <span className="text-3xs leading-4 text-ink-muted" data-testid="generation-charged">
        {charged ? 'Charged for the steps that ran.' : 'Nothing was charged.'}
      </span>
      <div className="flex gap-2">
        {/*
          A retry makes a *new* work, and the new one starts out «still going»
          — so with «Went wrong» chosen it is not on this screen at all, and
          the card that was pressed would otherwise sit there looking as if
          nothing had happened. It says what happened instead, which is also
          true when no filter is on and the new work is right there.
        */}
        {started ? (
          <span className="text-3xs leading-4 text-ink-muted" data-testid="generation-restarted">
            Started again — look under «Still going».
          </span>
        ) : (
          <button
            className="cursor-pointer text-3xs leading-4 text-ink underline disabled:opacity-50"
            data-testid="generation-retry"
            disabled={pending}
            onClick={onRetry}
            type="button"
          >
            {pending ? 'Starting…' : 'Try it again'}
          </button>
        )}
        <button
          className="cursor-pointer text-3xs leading-4 text-ink-muted underline hover:text-ink"
          data-testid="generation-dismiss"
          onClick={onDismiss}
          type="button"
        >
          Put it away
        </button>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/generations')({
  beforeLoad: async ({ location }) => {
    const { data } = await authClient.getSession();
    if (!data) throw redirect({ to: '/login', search: { redirectTo: location.href } });
  },
  component: Generations,
});
